/**
 * The restaurant this device signs into.
 *
 * A login is `name@restaurantCode`. The code is a property of the workplace,
 * not of the person: every waiter in one cafe shares it, it never changes, and
 * nobody who works there thinks of it as part of their name. Asking a waiter to
 * type `aigul@alatoo` at the start of every shift is asking them to retype a
 * constant — and it is the half they are most likely to get wrong, because it
 * is the half they were never told.
 *
 * So the device remembers it after the first successful sign-in and thereafter
 * shows the restaurant's name and asks only for the short name. The full form
 * still works, and stays the way in on a device that has never been used, or
 * when somebody needs to sign into a different restaurant.
 *
 * Stored per device rather than per user: a shared tablet on a counter has one
 * restaurant and many people.
 */

const STORAGE_KEY = 'nova-restaurant-code';

export interface RememberedRestaurant {
  code: string;
  name: string;
}

/** Deliberately tolerant: a corrupt or absent entry simply means "not known". */
export const getRememberedRestaurant = (): RememberedRestaurant | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<RememberedRestaurant>;
    if (!parsed?.code || !parsed?.name) return null;

    return { code: String(parsed.code), name: String(parsed.name) };
  } catch {
    return null;
  }
};

export const rememberRestaurant = (restaurant: RememberedRestaurant): void => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ code: restaurant.code, name: restaurant.name })
    );
  } catch {
    // Private browsing, or a full quota. Signing in must not depend on this —
    // it is a convenience, and the full `name@code` form still works.
  }
};

export const forgetRestaurant = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* see above */
  }
};

/**
 * Build the login to submit.
 *
 * A value the person typed WITH a code is taken as written — that is how they
 * reach a different restaurant from a device that remembers one. Otherwise the
 * remembered code is appended.
 */
export const composeSubmittedLogin = (typed: string, code: string | null): string => {
  const value = typed.trim().toLowerCase();
  if (value.includes('@') || !code) return value;
  return `${value}@${code}`;
};
